/**
 * Server state for Ideas (packages/contracts ideas.ts, routes.step9 `ideas.*`):
 *   GET /ideas (every card at the chosen stage, newest first, plus the four tiles and the stage pill counts — the
 *   server shows deciders every idea and everyone else their own plus approved, building and live ones),
 *   GET /ideas/{id} (the drawer: trail and proof), POST /ideas ("Suggest an idea"),
 *   POST /ideas/{id}/decide (approve / ask for detail / decline), POST /ideas/{id}/reply ("Add the detail asked for"),
 *   POST /ideas/{id}/log (start building, mark live with proof, result looks right).
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { IDEA_STATUS_LABEL, type Idea, type IdeaPage, type IdeaStage, type IdeaStatus } from '@amplispot/contracts';
import { ApiFailure, call } from '../api';
import { refreshAfterWrite } from './work';

export const ikeys = {
  all: ['ideas'] as const,
  list: (stage: IdeaStage) => ['ideas', 'list', stage] as const,
  one: (id: string) => ['ideas', 'one', id] as const,
};

/** Prototype IDEA_STAGES — the pills after "All", in order. */
export const IDEA_STAGES: [Exclude<IdeaStage, 'all'>, string][] = [
  ['wait', 'Waiting for a decision'],
  ['approved', 'Approved'],
  ['building', 'Being built'],
  ['live', 'Live'],
  ['declined', 'Declined'],
];

/** Prototype ISTAT: the chip tone for each status (labels from the contract). */
const TONE: Record<IdeaStatus, string> = { new: 'warm', needs_info: 'info', approved: 'brand', building: 'brand', live: 'hot', declined: '' };
export const ideaChip = (s: IdeaStatus) => ({ tone: TONE[s], label: IDEA_STATUS_LABEL[s] });

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const istDate = (ts: string) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date(ts));

/** "3 Oct" for a business date (YYYY-MM-DD) — the prototype's target and trail dates. */
export function shortDate(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${d ?? 1} ${MON[(m ?? 1) - 1]}`;
}

/** "3 Oct", or "Today" for something from today in IST (prototype `when:'Today'` on a fresh idea). */
export function whenLabel(ts: string, today: string): string {
  const d = istDate(ts);
  return d === today ? 'Today' : shortDate(d);
}

export function refreshIdeas(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ikeys.all });
  // The menu badge (ideas waiting / detail asked) and the Today "ideas waiting" line come from /me and work.
  refreshAfterWrite(qc);
}

// ---------------------------------------------------------------- reads

const PAGE = 200;

/** GET /ideas at one stage, every page. The tiles and the pill counts come with the first page. */
export function useIdeas(stage: IdeaStage) {
  return useQuery({
    queryKey: ikeys.list(stage),
    queryFn: async (): Promise<Pick<IdeaPage, 'data' | 'stats' | 'stage_counts'>> => {
      const first = await call('ideas.list', { query: { limit: PAGE, stage } });
      const data: Idea[] = [...first.data];
      let cursor = first.next_cursor;
      while (cursor) {
        const next = await call('ideas.list', { query: { limit: PAGE, stage, cursor } });
        data.push(...next.data);
        cursor = next.next_cursor;
      }
      return { data, stats: first.stats, stage_counts: first.stage_counts };
    },
    placeholderData: (prev) => prev,
  });
}

export function useIdea(id: string | null) {
  return useQuery({
    queryKey: ikeys.one(id ?? ''),
    queryFn: () => call('ideas.get', { params: { id: id! } }) as Promise<Idea>,
    enabled: !!id,
    retry: (n, e) => !(e instanceof ApiFailure && (e.status === 404 || e.status === 403)) && n < 2,
  });
}

// ---------------------------------------------------------------- writes

/** A write that refreshes the list and the drawer; a 409/412 (someone else acted first) refreshes too. */
export function useIdeaWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => refreshIdeas(qc),
    onError: (e) => {
      if (e instanceof ApiFailure && (e.status === 409 || e.status === 412)) refreshIdeas(qc);
    },
  });
}

export type { Idea };
